import React, { useState, useEffect } from 'react';
import '../assets/TuteeInvoiceAdjustmentModal.css';
import { adminApi } from '../services/api';

const TuteeInvoiceAdjustmentModal = ({ invoice, onClose, onAdjustmentAdded }) => {
  const [formData, setFormData] = useState({
    amount: '',
    category: 'discount',
    description: '',
    affectsProfitShare: true
  });
  
  const [existingAdjustments, setExistingAdjustments] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAdjustment, setEditingAdjustment] = useState(null);
  const [impactPreview, setImpactPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAdjustments, setLoadingAdjustments] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Profit sharer selection state
  const [profitSharers, setProfitSharers] = useState([]);
  const [selectedProfitSharers, setSelectedProfitSharers] = useState([]);
  const [targetMonth, setTargetMonth] = useState(() => {
    // Default to next month
    const nextMonth = invoice.month === 12 ? 1 : invoice.month + 1;
    return nextMonth;
  });
  const [targetYear, setTargetYear] = useState(() => {
    return invoice.month === 12 ? invoice.year + 1 : invoice.year;
  });

  // Category options
  const categories = [
    { value: 'discount', label: 'Discount' },
    { value: 'refund', label: 'Refund' },
    { value: 'correction', label: 'Correction' },
    { value: 'late_fee', label: 'Late Fee' },
    { value: 'materials_reimbursement', label: 'Materials Reimbursement' },
    { value: 'other', label: 'Other' }
  ];

  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' },
    { value: 3, label: 'March' }, { value: 4, label: 'April' },
    { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' },
    { value: 9, label: 'September' }, { value: 10, label: 'October' },
    { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear + i - 1);

  // Fetch existing adjustments on mount
  useEffect(() => {
    fetchAdjustments();
  }, [invoice._id]);

  // Fetch profit sharers when affect profit share is toggled on
  useEffect(() => {
    if (formData.affectsProfitShare && profitSharers.length === 0) {
      fetchProfitSharers();
    }
  }, [formData.affectsProfitShare]);

  const fetchAdjustments = async () => {
    setLoadingAdjustments(true);
    try {
      const response = await adminApi.getTuteeInvoiceAdjustments(invoice._id);
      if (response.data.success) {
        setExistingAdjustments(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching adjustments:', err);
      setExistingAdjustments([]);
    } finally {
      setLoadingAdjustments(false);
    }
  };

  const fetchProfitSharers = async () => {
    try {
      const response = await adminApi.previewTuteeInvoiceAdjustment(1, targetMonth, targetYear);
      if (response.data.success && response.data.data.profitSharerImpact) {
        const sharers = response.data.data.profitSharerImpact;
        setProfitSharers(sharers);
        setSelectedProfitSharers(sharers.map(ps => ps.profitSharerId));
      }
    } catch (err) {
      console.error('Error fetching profit sharers:', err);
    }
  };

  // Calculate impact preview when amount or target month changes
  useEffect(() => {
    const calculateImpact = async () => {
      if (!formData.amount || formData.amount === '0') {
        setImpactPreview(null);
        return;
      }

      try {
        const response = await adminApi.previewTuteeInvoiceAdjustment(
          parseFloat(formData.amount), targetMonth, targetYear
        );
        if (response.data.success) {
          setImpactPreview(response.data.data);
          // Also update profit sharers list from impact data
          if (response.data.data.profitSharerImpact) {
            const sharers = response.data.data.profitSharerImpact;
            setProfitSharers(sharers);
            // Only set selected on first load
            if (selectedProfitSharers.length === 0) {
              setSelectedProfitSharers(sharers.map(ps => ps.profitSharerId));
            }
          }
        }
      } catch (err) {
        console.error('Error calculating impact:', err);
      }
    };

    const debounce = setTimeout(calculateImpact, 500);
    return () => clearTimeout(debounce);
  }, [formData.amount, targetMonth, targetYear]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const handleProfitSharerToggle = (profitSharerId) => {
    setSelectedProfitSharers(prev => {
      if (prev.includes(profitSharerId)) {
        return prev.filter(id => id !== profitSharerId);
      } else {
        return [...prev, profitSharerId];
      }
    });
  };

  const handleDeleteAdjustment = async (adjustmentId) => {
    if (!window.confirm('Are you sure you want to delete this adjustment? This will also remove any related profit sharer commission adjustments.')) {
      return;
    }

    setDeletingId(adjustmentId);
    setError('');

    try {
      const response = await adminApi.deleteTuteeInvoiceAdjustment(invoice._id, adjustmentId);
      if (response.data.success) {
        setSuccess('Adjustment deleted successfully');
        fetchAdjustments();
        onAdjustmentAdded(response.data.data.invoice);
      } else {
        setError(response.data.message || 'Failed to delete adjustment');
      }
    } catch (err) {
      console.error('Error deleting adjustment:', err);
      setError(err.response?.data?.message || 'An error occurred while deleting adjustment');
    } finally {
      setDeletingId(null);
    }
  };

  const handleEditAdjustment = (adj) => {
    setEditingAdjustment(adj);
    setFormData({
      amount: adj.amount.toString(),
      category: adj.category,
      description: adj.description,
      affectsProfitShare: adj.affectsProfitShare
    });
    setShowAddForm(true);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.amount || parseFloat(formData.amount) === 0) {
      setError('Please enter a non-zero amount');
      return;
    }

    if (!formData.description.trim()) {
      setError('Please enter a description');
      return;
    }

    setLoading(true);

    try {
      let response;

      if (editingAdjustment) {
        // Edit: delete old + create new
        response = await adminApi.deleteTuteeInvoiceAdjustment(invoice._id, editingAdjustment._id);
        if (!response.data.success) {
          setError('Failed to update adjustment');
          setLoading(false);
          return;
        }
        response = await adminApi.addTuteeInvoiceAdjustment(invoice._id, {
          amount: parseFloat(formData.amount),
          category: formData.category,
          description: formData.description.trim(),
          affectsProfitShare: formData.affectsProfitShare,
          selectedProfitSharers: formData.affectsProfitShare ? selectedProfitSharers : [],
          targetMonth: formData.affectsProfitShare ? targetMonth : null,
          targetYear: formData.affectsProfitShare ? targetYear : null
        });
      } else {
        // Add new
        response = await adminApi.addTuteeInvoiceAdjustment(invoice._id, {
          amount: parseFloat(formData.amount),
          category: formData.category,
          description: formData.description.trim(),
          affectsProfitShare: formData.affectsProfitShare,
          selectedProfitSharers: formData.affectsProfitShare ? selectedProfitSharers : [],
          targetMonth: formData.affectsProfitShare ? targetMonth : null,
          targetYear: formData.affectsProfitShare ? targetYear : null
        });
      }

      if (response.data.success) {
        setSuccess(editingAdjustment ? 'Adjustment updated successfully!' : 'Adjustment added successfully!');
        setFormData({
          amount: '',
          category: 'discount',
          description: '',
          affectsProfitShare: true
        });
        setShowAddForm(false);
        setEditingAdjustment(null);
        fetchAdjustments();
        onAdjustmentAdded(response.data.data.invoice);
      } else {
        setError(response.data.message || 'Failed to save adjustment');
      }
    } catch (err) {
      console.error('Error saving adjustment:', err);
      setError(err.response?.data?.message || 'An error occurred while saving adjustment');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: 'SGD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-SG', {
      timeZone: 'Asia/Singapore',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  const totalAdjustments = existingAdjustments.reduce((sum, adj) => sum + adj.amount, 0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content adjustment-modal large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Manage Adjustments</h2>
          <button className="modal-close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {/* Invoice Info */}
          <div className="invoice-info-section">
            <h3>Invoice Details</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Invoice Number:</span>
                <span className="info-value">{invoice.invoiceNumber}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Tutee:</span>
                <span className="info-value">{invoice.tutee?.name}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Base Amount:</span>
                <span className="info-value">{formatCurrency(invoice.subtotal || 0)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Total Adjustments:</span>
                <span className={`info-value ${totalAdjustments >= 0 ? 'positive' : 'negative'}`}>
                  {totalAdjustments >= 0 ? '+' : ''}{formatCurrency(totalAdjustments)}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Final Amount:</span>
                <span className="info-value strong">{formatCurrency(invoice.finalAmount)}</span>
              </div>
            </div>
          </div>

          {/* Existing Adjustments List */}
          <div className="adjustments-list-section">
            <div className="section-header">
              <h3>Existing Adjustments ({existingAdjustments.length})</h3>
              {!showAddForm && (
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={() => setShowAddForm(true)}
                >
                  + Add Adjustment
                </button>
              )}
            </div>

            {loadingAdjustments ? (
              <div className="loading-state">Loading adjustments...</div>
            ) : existingAdjustments.length === 0 ? (
              <div className="no-adjustments">
                <p>No adjustments have been added to this invoice yet.</p>
              </div>
            ) : (
              <div className="adjustments-table-container">
                <table className="adjustments-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Category</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Applied By</th>
                      <th>Profit Share</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {existingAdjustments.map((adj, index) => (
                      <tr key={adj._id || index}>
                        <td>{formatDate(adj.appliedAt)}</td>
                        <td>
                          <span className="category-badge">{getCategoryLabel(adj.category)}</span>
                        </td>
                        <td>{adj.description}</td>
                        <td className={adj.amount >= 0 ? 'amount-positive' : 'amount-negative'}>
                          {adj.amount >= 0 ? '+' : ''}{formatCurrency(adj.amount)}
                        </td>
                        <td>{adj.appliedBy?.name || 'System'}</td>
                        <td>
                          {adj.affectsProfitShare ? (
                            <span className="badge badge-yes">Yes</span>
                          ) : (
                            <span className="badge badge-no">No</span>
                          )}
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn btn-edit btn-xs"
                              onClick={() => handleEditAdjustment(adj)}
                              title="Edit this adjustment"
                            >
                              ✎
                            </button>
                            <button
                              className="btn btn-danger btn-xs"
                              onClick={() => handleDeleteAdjustment(adj._id)}
                              disabled={deletingId === adj._id}
                              title="Delete this adjustment"
                            >
                              {deletingId === adj._id ? '...' : '✕'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Add Adjustment Form */}
          {showAddForm && (
            <div className="add-adjustment-section">
              <div className="section-header">
                <h3>{editingAdjustment ? 'Edit Adjustment' : 'Add New Adjustment'}</h3>
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingAdjustment(null);
                    setFormData({
                      amount: '',
                      category: 'discount',
                      description: '',
                      affectsProfitShare: true
                    });
                    setError('');
                    setImpactPreview(null);
                  }}
                >
                  Cancel
                </button>
              </div>

              <form onSubmit={handleSubmit} className="adjustment-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="amount">
                      Amount <span className="required">*</span>
                      <span className="help-text">(Use negative for deductions, positive for additions)</span>
                    </label>
                    <input
                      type="number"
                      id="amount"
                      name="amount"
                      value={formData.amount}
                      onChange={handleChange}
                      step="0.01"
                      placeholder="e.g., -50.00 or 25.00"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="category">
                      Category <span className="required">*</span>
                    </label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                    >
                      {categories.map(cat => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="description">
                    Description <span className="required">*</span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Enter reason for adjustment..."
                    maxLength="500"
                    required
                  />
                  <span className="char-count">{formData.description.length}/500</span>
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="affectsProfitShare"
                      checked={formData.affectsProfitShare}
                      onChange={handleChange}
                    />
                    <span>Affect profit sharer commission</span>
                  </label>
                  <p className="help-text">
                    If checked, this will create a commission adjustment (opposite sign) for the selected profit sharers in the chosen month.
                  </p>
                </div>

                {/* Profit Sharer Selection */}
                {formData.affectsProfitShare && profitSharers.length > 0 && (
                  <div className="profit-sharer-selection">
                    <h4>Select Profit Sharers to Affect</h4>
                    <p className="help-text">
                      The same proportional amount will be added/deducted from each selected profit sharer's tutor invoice for the chosen month.
                      {formData.amount && (
                        <> E.g., tutee adjustment of <strong>{formatCurrency(parseFloat(formData.amount))}</strong> → profit sharers get the same sign proportionally.</>
                      )}
                    </p>

                    {/* Target Month/Year */}
                    <div className="target-period-row">
                      <label>Apply to month:</label>
                      <select
                        value={targetMonth}
                        onChange={(e) => setTargetMonth(parseInt(e.target.value))}
                        className="target-month-select"
                      >
                        {months.map(m => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </select>
                      <select
                        value={targetYear}
                        onChange={(e) => setTargetYear(parseInt(e.target.value))}
                        className="target-year-select"
                      >
                        {years.map(y => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>

                    {/* Profit Sharer List */}
                    <div className="profit-sharer-list">
                      {profitSharers.map(ps => {
                        const isSelected = selectedProfitSharers.includes(ps.profitSharerId);
                        const impact = formData.amount
                          ? (parseFloat(formData.amount) * ps.commissionRate) / 100
                          : 0;
                        const isEndorsed = ps.endorsementStatus === 'endorsed';
                        const isPaid = ps.paymentStatus === 'paid';
                        
                        return (
                          <div 
                            key={ps.profitSharerId} 
                            className={`profit-sharer-item ${isSelected ? 'selected' : 'unselected'}`}
                          >
                            <label className="profit-sharer-label">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleProfitSharerToggle(ps.profitSharerId)}
                              />
                              <span className="ps-name">{ps.profitSharerName}</span>
                              <span className="ps-rate">({ps.commissionRate}%)</span>
                              {isPaid && (
                                <span className="ps-status-badge paid" title="Already paid for this month">PAID</span>
                              )}
                              {isEndorsed && !isPaid && (
                                <span className="ps-status-badge endorsed" title="Already endorsed for this month">ENDORSED</span>
                              )}
                            </label>
                            {isSelected && formData.amount && (
                              <span className={`ps-impact ${impact >= 0 ? 'positive' : 'negative'}`}>
                                {impact >= 0 ? '+' : ''}{formatCurrency(impact)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {selectedProfitSharers.length === 0 && (
                      <p className="warning-text">No profit sharers selected — adjustment won't affect anyone's commission.</p>
                    )}
                  </div>
                )}

                {/* Impact Preview */}
                {impactPreview && formData.affectsProfitShare && (
                  <div className="impact-preview">
                    <h4>Impact Preview</h4>
                    <div className="impact-summary">
                      <div className="impact-item">
                        <span className="impact-label">New Invoice Total:</span>
                        <span className="impact-value">
                          {formatCurrency(invoice.finalAmount + parseFloat(formData.amount || 0))}
                        </span>
                      </div>
                      <div className="impact-item">
                        <span className="impact-label">Change:</span>
                        <span className={`impact-value ${parseFloat(formData.amount) >= 0 ? 'positive' : 'negative'}`}>
                          {parseFloat(formData.amount) >= 0 ? '+' : ''}{formatCurrency(parseFloat(formData.amount || 0))}
                        </span>
                      </div>
                      <div className="impact-item">
                        <span className="impact-label">Profit sharer deductions applied to:</span>
                        <span className="impact-value">
                          {months.find(m => m.value === targetMonth)?.label} {targetYear}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Error/Success Messages */}
                {error && <div className="error-message">{error}</div>}
                {success && <div className="success-message">{success}</div>}

                {/* Form Actions */}
                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : editingAdjustment ? 'Update Adjustment' : 'Add Adjustment'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Messages outside form */}
          {!showAddForm && error && <div className="error-message">{error}</div>}
          {!showAddForm && success && <div className="success-message">{success}</div>}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TuteeInvoiceAdjustmentModal;
